/* ==========================================================================
   FLC Forge — Accordion
   ========================================================================== */

class FLCAccordion extends HTMLElement {
  connectedCallback() {
    this.items      = this.querySelectorAll('[data-accordion-item]');
    this.allowMulti = this.dataset.allowMultiple === 'true';

    this.items.forEach(item => {
      const trigger = item.querySelector('[data-accordion-trigger]');
      const content = item.querySelector('[data-accordion-content]');

      if (!trigger || !content) return;

      // Set initial ARIA state
      const isOpen = item.dataset.open === 'true';
      trigger.setAttribute('aria-expanded', isOpen);
      content.hidden = !isOpen;
      item.classList.toggle('is-open', isOpen);

      trigger.addEventListener('click', () => this.toggle(item, trigger, content));

      trigger.addEventListener('keydown', (e) => {
        const items = [...this.items];
        const idx   = items.indexOf(item);
        if (e.key === 'ArrowDown') { e.preventDefault(); items[idx + 1]?.querySelector('[data-accordion-trigger]')?.focus(); }
        if (e.key === 'ArrowUp')   { e.preventDefault(); items[idx - 1]?.querySelector('[data-accordion-trigger]')?.focus(); }
        if (e.key === 'Home')      { e.preventDefault(); items[0]?.querySelector('[data-accordion-trigger]')?.focus(); }
        if (e.key === 'End')       { e.preventDefault(); items[items.length - 1]?.querySelector('[data-accordion-trigger]')?.focus(); }
      });
    });
  }

  toggle(item, trigger, content) {
    const isOpen = trigger.getAttribute('aria-expanded') === 'true';

    if (!this.allowMulti && !isOpen) {
      // Close all others
      this.items.forEach(other => {
        if (other !== item) {
          const t = other.querySelector('[data-accordion-trigger]');
          const c = other.querySelector('[data-accordion-content]');
          const otherIsOpen = t?.getAttribute('aria-expanded') === 'true';
          if (t) t.setAttribute('aria-expanded', 'false');
          if (c && otherIsOpen) this.animateClose(c);
          other.classList.remove('is-open');
        }
      });
    }

    if (isOpen) {
      trigger.setAttribute('aria-expanded', 'false');
      item.classList.remove('is-open');
      this.animateClose(content);
    } else {
      trigger.setAttribute('aria-expanded', 'true');
      item.classList.add('is-open');
      this.animateOpen(content);
    }
  }

  animateOpen(content) {
    content.hidden = false;
    const targetH = content.scrollHeight;
    content.style.height = '0';
    content.style.overflow = 'hidden';
    content.style.transition = 'height 0.3s ease';
    requestAnimationFrame(() => { content.style.height = targetH + 'px'; });
    content.addEventListener('transitionend', () => {
      content.style.height = '';
      content.style.overflow = '';
      content.style.transition = '';
    }, { once: true });
  }

  animateClose(content) {
    content.style.height = content.scrollHeight + 'px';
    content.style.overflow = 'hidden';
    content.style.transition = 'height 0.3s ease';
    requestAnimationFrame(() => { content.style.height = '0'; });
    content.addEventListener('transitionend', () => {
      content.hidden = true;
      content.style.height = '';
      content.style.overflow = '';
      content.style.transition = '';
    }, { once: true });
  }
}

function initAccordionGroups(scope = document) {
  scope.querySelectorAll('[data-accordion-groups]').forEach(group => {
    if (group.dataset.tabsBound === 'true') return;
    group.dataset.tabsBound = 'true';

    const tabs = [...group.querySelectorAll('[data-group-tab]')];
    const panels = [...group.querySelectorAll('[data-group-panel]')];

    if (tabs.length <= 1 || !panels.length) return;

    const activate = (groupId, focusTab = false) => {
      tabs.forEach(tab => {
        const isActive = tab.dataset.groupTab === groupId;
        tab.classList.toggle('is-active', isActive);
        tab.setAttribute('aria-selected', isActive ? 'true' : 'false');
        tab.setAttribute('tabindex', isActive ? '0' : '-1');
        if (isActive && focusTab) tab.focus();
      });

      panels.forEach(panel => {
        const isActive = panel.dataset.groupPanel === groupId;
        panel.classList.toggle('is-active', isActive);
        panel.hidden = !isActive;
      });
    };

    tabs.forEach((tab, index) => {
      tab.addEventListener('click', () => activate(tab.dataset.groupTab, false));
      tab.addEventListener('keydown', (event) => {
        let targetIndex = null;
        if (event.key === 'ArrowRight' || event.key === 'ArrowDown') targetIndex = (index + 1) % tabs.length;
        if (event.key === 'ArrowLeft' || event.key === 'ArrowUp') targetIndex = (index - 1 + tabs.length) % tabs.length;
        if (event.key === 'Home') targetIndex = 0;
        if (event.key === 'End') targetIndex = tabs.length - 1;

        if (targetIndex === null) return;
        event.preventDefault();
        activate(tabs[targetIndex].dataset.groupTab, true);
      });
    });

    const initialTab = tabs.find(tab => tab.classList.contains('is-active')) || tabs[0];
    if (initialTab) activate(initialTab.dataset.groupTab, false);
  });
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => initAccordionGroups());
} else {
  initAccordionGroups();
}

document.addEventListener('shopify:section:load', event => {
  initAccordionGroups(event.target);
});

customElements.define('flc-accordion', FLCAccordion);
